import { apiAxios } from '@/api/apiConfig';
import { EnterpriseContract } from '@/api/planApi';

export interface TeamSummary {
  teamId: number;
  teamName: string;
  myRole: 'OWNER' | 'MEMBER';
  memberCount?: number;
  plan?: string;
  monthlyLimit?: number;
  status?: string;
  ownerUserId?: string;
}

export interface TeamDetail extends TeamSummary {
  ownerUserId: string;
  createdAt: string;
  approvalWorkflowEnabled?: boolean;
}

export interface AltReviewImage {
  id: number;
  url: string;
  imageSrc: string;
  displayImageUrl?: string;
  generatedAlt: string | null;
  originalAlt?: string | null;
  altGenerationType?: string;
  updatedAt: string;
  altApprovalStatus?: string;
}

export interface AltReviewPage {
  content: AltReviewImage[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  size: number;
}

export interface AltReviewHistoryItem {
  imageId: number;
  url: string | null;
  imageSrc: string | null;
  displayImageUrl: string | null;
  generatedAlt: string | null;
  altGenerationType: string | null;
  status: 'APPROVED' | 'REJECTED';
  note: string | null;
  reviewerUserId: string;
  reviewedAt: string;
}

export interface AltReviewHistoryPage {
  content: AltReviewHistoryItem[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  size: number;
}

/** 팀 플랜 변경 응답 */
export interface TeamPlanChangeResponse {
  success: boolean;
  message: string | null;
  changeType?: 'UPGRADE' | 'DOWNGRADE' | 'NONE';
  effectiveDate?: string;
  paymentMethod?: 'CARD' | 'BANK_TRANSFER';
  amount?: number;
}

export interface TeamMember {
  userId: string;
  name: string;
  email: string;
  role: 'OWNER' | 'MEMBER';
  status: 'ACTIVE' | 'PENDING_REMOVAL';
  joinedAt: string;
}

export interface TeamUsage {
  teamId: number;
  teamName: string;
  month: string;
  totalUsed: number;
  monthlyLimit: number;
  remaining: number;
  billingStartDate: string | null;  // 팀 청구 기간 시작일
  billingEndDate: string | null;    // 팀 청구 기간 종료일
  memberStats: {
    userId: string;
    name: string;
    role: string;
    usedCount: number;
  }[];
  enterpriseContract: EnterpriseContract | null;
}

export interface InviteAcceptResult {
  teamId: number;
  teamName: string;
  role: 'OWNER' | 'MEMBER';
  joinedAt: string;
}

export interface InviteVerifyResult {
  valid: boolean;
  teamId?: number;
  teamName?: string;
  role?: 'OWNER' | 'MEMBER';
  invitedEmail?: string;
  expiresAt?: string;
  message?: string;
}

export interface RemoveMemberResult {
  removed: boolean;
  pendingRemoval: boolean;
}

export interface TransferOwnerResult {
  newOwner: { userId: string; role: 'OWNER' };
  prevOwner: { userId: string; role: 'MEMBER' };
}

// apiAxios의 응답 인터셉터(src/api/apiConfig.ts)가 {success, data} 래퍼를
// 이미 벗겨서 response.data = data 로 만들어준다. 따라서 아래 제네릭 타입은
// 래퍼가 아니라 실제로 unwrap된 payload 타입 그대로 지정해야 한다.
// (과거 {success, data: T} 형태로 잘못 타입 지정된 채 방치되어 있었고,
// 호출부에서 res.data.success / res.data.data 를 참조하는 버그가 다수 있었음)
export const teamApi = {
  getMyTeams: () =>
    apiAxios.get<TeamSummary[]>('/teams/my'),

  getAdminTeams: () =>
    apiAxios.get<TeamSummary[] | { content: TeamSummary[] }>('/admin/teams?page=0&size=100'),

  getTeam: (teamId: number) =>
    apiAxios.get<TeamDetail>(`/teams/${teamId}`),

  createTeam: (name: string, paymentMethod?: 'CARD' | 'BANK_TRANSFER') =>
    apiAxios.post<TeamDetail>('/teams', { name, paymentMethod }),

  updateTeam: (teamId: number, name: string) =>
    apiAxios.patch<TeamDetail>(`/teams/${teamId}`, { name }),

  deleteTeam: (teamId: number) =>
    apiAxios.delete<void>(`/teams/${teamId}`),

  deleteTeamAdmin: (teamId: number) =>
    apiAxios.delete<void>(`/admin/teams/${teamId}`),

  getMembers: (teamId: number) =>
    apiAxios.get<TeamMember[]>(`/teams/${teamId}/members`),

  inviteMember: (teamId: number, email: string) =>
    apiAxios.post<null>(
      `/teams/${teamId}/invites`, { invitedEmail: email, role: 'MEMBER' }
    ),

  verifyInvite: (token: string) =>
    apiAxios.get<InviteVerifyResult>(
      `/teams/invites/${token}/verify`
    ),

  acceptInvite: (token: string) =>
    apiAxios.post<InviteAcceptResult>(
      `/teams/invites/${token}/accept`, {}, { _skipAuthRedirect: true } as any
    ),

  cancelInvite: (inviteToken: string) =>
    apiAxios.delete<void>(`/teams/invites/${inviteToken}`),

  changeMemberRole: (teamId: number, userId: string, role: 'MEMBER') =>
    apiAxios.patch<void>(
      `/teams/${teamId}/members/${userId}/role`, { newRole: role }
    ),

  removeMember: (teamId: number, userId: string) =>
    apiAxios.delete<RemoveMemberResult>(
      `/teams/${teamId}/members/${userId}`
    ),

  transferOwner: (teamId: number, newOwnerUserId: string) =>
    apiAxios.patch<TransferOwnerResult>(
      `/teams/${teamId}/transfer-owner`, { newOwnerUserId }
    ),

  changeTeamPlan: (teamId: number, plan: 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE', paymentMethod?: 'CARD' | 'BANK_TRANSFER') =>
    apiAxios.patch<TeamPlanChangeResponse>(
      `/teams/${teamId}/plan`, { plan, paymentMethod }
    ),

  getUsage: (teamId: number, month?: string) =>
    apiAxios.get<TeamUsage>(
      `/teams/${teamId}/usage${month ? `?month=${month}` : ''}`
    ),

  leaveTeam: (teamId: number) =>
    apiAxios.post<{ pendingRemovalDate: string; restoredPlan: string }>(
      `/teams/${teamId}/leave`, {}
    ),

  updateApprovalWorkflow: (teamId: number, enabled: boolean) =>
    apiAxios.patch<TeamDetail>(
      `/teams/${teamId}/approval-workflow`, { enabled }
    ),

  getPendingAltReviews: (teamId: number, page = 0, size = 20) =>
    apiAxios.get<AltReviewPage>(
      `/teams/${teamId}/alt-reviews/pending?page=${page}&size=${size}`
    ),

  getAltReviewHistory: (teamId: number, page = 0, size = 20) =>
    apiAxios.get<AltReviewHistoryPage>(
      `/teams/${teamId}/alt-reviews/history?page=${page}&size=${size}`
    ),

  getMyPendingAltReviews: (teamId: number, page = 0, size = 20) =>
    apiAxios.get<AltReviewPage>(
      `/teams/${teamId}/alt-reviews/my-pending?page=${page}&size=${size}`
    ),

  getMyAltReviewHistory: (teamId: number, page = 0, size = 20) =>
    apiAxios.get<AltReviewHistoryPage>(
      `/teams/${teamId}/alt-reviews/my-history?page=${page}&size=${size}`
    ),

  approveAltReview: (teamId: number, imageId: number, note?: string) =>
    apiAxios.post<void>(
      `/teams/${teamId}/alt-reviews/${imageId}/approve`, note ? { note } : {}
    ),

  rejectAltReview: (teamId: number, imageId: number, note?: string) =>
    apiAxios.post<void>(
      `/teams/${teamId}/alt-reviews/${imageId}/reject`, note ? { note } : {}
    ),
};
